/**
 * CSV Export Utility
 * Provides functions to export call data, next steps reports, and analytics to CSV format
 */

interface ExportableCall {
  call_id: string;
  customer_name?: string;
  customer_phone?: string;
  start_time?: string;
  end_time?: string;
  duration?: number;
  channel_type?: string;
  issue_category?: string;
  issue_details?: string;
  urgency_level?: string;
  resolution_status?: string;
  resolved?: boolean;
  callback_requested?: boolean;
  callback_status?: string;
  human_transfer_requested?: boolean;
  escalation_requested?: boolean;
  under_30_seconds?: boolean;
  troubleshooting_steps?: string;
  ai_confidence?: string;
  message_count?: number;
  exchange_count?: number;
}

interface ExportableNextStep {
  call_id: string;
  customer_name?: string;
  customer_phone?: string;
  call_date?: string;
  duration?: number;
  issue_category?: string;
  action_required?: string;
  action_type?: string;
  priority?: string;
  status?: string;
  callback_requested?: boolean;
  human_transfer_requested?: boolean;
  escalation_requested?: boolean;
  under_30_seconds?: boolean;
  channel_type?: string;
}

/**
 * Convert data to CSV format
 */
function convertToCSV(data: any[], headers: string[]): string {
  const csvRows: string[] = [];
  
  // Add header row
  csvRows.push(headers.join(','));
  
  // Add data rows
  data.forEach(row => {
    const values = headers.map(header => {
      const value = row[header];
      
      // Handle null/undefined
      if (value === null || value === undefined) {
        return '';
      }
      
      // Convert boolean to string
      if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
      }
      
      // Escape quotes and wrap in quotes if contains comma or newline
      const stringValue = String(value);
      if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      
      return stringValue;
    });
    csvRows.push(values.join(','));
  });
  
  return csvRows.join('\n');
}

/**
 * Download CSV file
 */
function downloadCSV(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
}

/**
 * Format duration in seconds to readable format
 */
function formatDuration(seconds?: number): string {
  if (!seconds || seconds <= 0) return '0:00';
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Format date to readable format
 */
function formatDate(dateString?: string): string {
  if (!dateString) return '';
  try {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch {
    return dateString;
  }
}

/**
 * Export calls data to CSV
 */
export function exportCallsToCSV(calls: ExportableCall[], filename?: string): void {
  const headers = [
    'call_id',
    'customer_name',
    'customer_phone',
    'start_time',
    'duration',
    'channel_type',
    'issue_category',
    'issue_details',
    'urgency_level',
    'resolution_status',
    'resolved',
    'callback_requested',
    'callback_status',
    'human_transfer_requested',
    'escalation_requested',
    'under_30_seconds',
    'ai_confidence',
    'message_count',
    'exchange_count'
  ];
  
  // Transform data for better readability
  const transformedData = calls.map(call => ({
    call_id: call.call_id,
    customer_name: call.customer_name || 'Unknown',
    customer_phone: call.customer_phone || 'N/A',
    start_time: formatDate(call.start_time),
    duration: call.channel_type === 'CHAT' && call.exchange_count 
      ? `${call.exchange_count} exchanges` 
      : formatDuration(call.duration),
    channel_type: call.channel_type || 'VOICE',
    issue_category: call.issue_category || 'N/A',
    issue_details: call.issue_details || 'N/A',
    urgency_level: call.urgency_level || 'Medium',
    resolution_status: call.resolution_status || (call.resolved ? 'Resolved' : 'Pending'),
    resolved: call.resolved ? 'Yes' : 'No',
    callback_requested: call.callback_requested ? 'Yes' : 'No',
    callback_status: call.callback_status || 'N/A',
    human_transfer_requested: call.human_transfer_requested ? 'Yes' : 'No',
    escalation_requested: call.escalation_requested ? 'Yes' : 'No',
    under_30_seconds: call.under_30_seconds ? 'Yes' : 'No',
    ai_confidence: call.ai_confidence ? `${(parseFloat(call.ai_confidence) * 100).toFixed(1)}%` : 'N/A',
    message_count: call.message_count || 'N/A',
    exchange_count: call.exchange_count || 'N/A'
  }));
  
  const csv = convertToCSV(transformedData, headers);
  const defaultFilename = `calls-export-${new Date().toISOString().split('T')[0]}.csv`;
  downloadCSV(csv, filename || defaultFilename);
}

/**
 * Export next steps report to CSV
 */
export function exportNextStepsToCSV(actions: ExportableNextStep[], filename?: string): void {
  const headers = [
    'call_id',
    'customer_name',
    'customer_phone',
    'call_date',
    'duration',
    'channel_type',
    'issue_category',
    'issue_details',
    'call_summary',
    'action_required',
    'action_type',
    'priority',
    'status',
    'callback_requested',
    'human_transfer_requested',
    'escalation_requested',
    'under_30_seconds'
  ];
  
  // Transform data for better readability
  const transformedData = actions.map(action => ({
    call_id: action.call_id,
    customer_name: action.customer_name || 'Unknown',
    customer_phone: action.customer_phone || 'N/A',
    call_date: formatDate(action.call_date),
    duration: formatDuration(action.duration),
    channel_type: action.channel_type || 'VOICE',
    issue_category: action.issue_category || 'N/A',
    issue_details: (action as any).issue_details || 'N/A',
    call_summary: (action as any).call_summary || 'N/A',
    action_required: action.action_required || 'Review required',
    action_type: action.action_type || 'Follow Up',
    priority: action.priority || 'Medium',
    status: action.status || 'Pending',
    callback_requested: action.callback_requested ? 'Yes' : 'No',
    human_transfer_requested: action.human_transfer_requested ? 'Yes' : 'No',
    escalation_requested: action.escalation_requested ? 'Yes' : 'No',
    under_30_seconds: action.under_30_seconds ? 'Yes' : 'No'
  }));
  
  const csv = convertToCSV(transformedData, headers);
  const defaultFilename = `next-steps-export-${new Date().toISOString().split('T')[0]}.csv`;
  downloadCSV(csv, filename || defaultFilename);
}

/**
 * Export filtered calls to CSV
 */
export function exportFilteredCallsToCSV(calls: ExportableCall[], filterDescription: string, filename?: string): void {
  exportCallsToCSV(calls, filename);
}

